import { Location } from 'react-router-dom';
import { TRAILING_SLASHES_REGEX } from 'core/utils/constants';
import { Customer, DutyPayer } from 'store/customers/customer';
import { mapCustomerToFormValues } from './tabs/overview/Utils';
import { REQUIRED_OVERVIEW_FIELDS } from './tabs/overview/types';

export const CustomerDetailsTabs = {
    OVERVIEW: 'overview',
    FILES: 'files',
    ADDITIONAL_CHARGES: 'additional-charges',
    JOBS: 'jobs',
    INVOICES: 'invoices',
};

const TAB_KEYS = [
    CustomerDetailsTabs.OVERVIEW,
    CustomerDetailsTabs.FILES,
    CustomerDetailsTabs.ADDITIONAL_CHARGES,
    CustomerDetailsTabs.JOBS,
    CustomerDetailsTabs.INVOICES,
];

type TabKey = typeof TAB_KEYS[number];

export type ConfigurationStatus = {
    overview: { complete: boolean };
    aep5: { required: boolean; complete: boolean };
};

/** The AEP5 document is only required when the party itself pays the duty. */
export const isAep5Required = (customer?: Customer | null): boolean =>
    customer?.customerDetails?.dutyPayer === DutyPayer.CONSIGNEE;

/**
 * The backend verifies the AEP5 document once it has been uploaded and reports the outcome on the party,
 * so `aep5Verified` is the single source of truth for whether the AEP5 side of the party is settled.
 */
export const isAep5Verified = (customer?: Customer | null): boolean => !!customer?.customerDetails?.aep5Verified;

/**
 * The overview tab is configured when every field the overview form requires is filled in and,
 * when the party itself pays the duty, the broker has confirmed it may use the party's TAN and the
 * AEP5 document has been verified.
 */
export const isOverviewComplete = (customer: Customer): boolean => {
    const values = mapCustomerToFormValues(customer);
    const requiredFieldsFilled = REQUIRED_OVERVIEW_FIELDS.every((field) => {
        const value = values[field];
        return typeof value === 'string' ? value.trim().length > 0 : value !== null && value !== undefined;
    });

    // The TAN confirmation is not part of REQUIRED_OVERVIEW_FIELDS, so it has to be checked on its own
    const aep5AuthorisationConfirmed = !!customer.customerDetails?.confirmedTanUsage;
    const aep5Complete = !isAep5Required(customer) || (isAep5Verified(customer) && aep5AuthorisationConfirmed);

    return requiredFieldsFilled && aep5Complete;
};

export const getConfigurationCompletion = (status: ConfigurationStatus) => {
    const isOverviewConfigured = status.overview.complete;
    const isFilesConfigured = !status.aep5.required || status.aep5.complete;

    return {
        isOverviewConfigured,
        isFilesConfigured,
        isComplete: isOverviewConfigured && isFilesConfigured,
    };
};

export const getActiveTab = (location: Location | undefined, customerId: string | undefined): TabKey => {
    const defaultTab = CustomerDetailsTabs.OVERVIEW;

    if (!customerId || !location) return defaultTab;

    const base = `/customers/${customerId}`.replace(TRAILING_SLASHES_REGEX, '');
    const pathname = location.pathname.replace(TRAILING_SLASHES_REGEX, '');

    if (pathname === base) return defaultTab;

    // Must start with /customer/:customerId
    const prefix = `${base}/`;
    if (!pathname.startsWith(prefix)) return defaultTab;

    // Extract first segment after the base
    const tabFromUrl = pathname.slice(prefix.length).split('/')[0];

    return TAB_KEYS.includes(tabFromUrl) ? tabFromUrl : defaultTab;
};

export const EMPTY = 'N/A';
